<script setup lang="ts">
import type { NuxtError } from '#app'
import { ArrowLeftIcon, RefreshCcwIcon, ShieldAlertIcon } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { resolveAppErrorCopy, type AppErrorData } from '~/composables/useAppError'

defineOptions({
  name: 'AppErrorPage',
})

const props = defineProps<{
  error: NuxtError<AppErrorData>
}>()

const errorData = computed(() => {
  return (props.error.data ?? {}) as AppErrorData
})

const statusCode = computed(() => props.error.statusCode ?? 500)
const errorCopy = computed(() => resolveAppErrorCopy(statusCode.value))
const title = computed(() => props.error.message || errorCopy.value.message)
const statusMessage = computed(() => props.error.statusMessage || errorCopy.value.statusMessage)
const description = computed(() => {
  return errorData.value.description || errorCopy.value.description
})
const retryTo = computed(() => errorData.value.retryTo || '/')
const retryLabel = computed(() => errorData.value.retryLabel || 'Try again')
const backTo = computed(() => errorData.value.backTo || '/')
const backLabel = computed(() => errorData.value.backLabel || 'Back home')
const request = computed(() => errorData.value.request || null)

async function handleRetry() {
  await clearError({ redirect: retryTo.value })
}

async function handleBack() {
  await clearError({ redirect: backTo.value })
}
</script>

<template>
  <div
    class="relative min-h-screen overflow-hidden bg-[linear-gradient(160deg,#f8fafc_0%,#eff6ff_46%,#fef3c7_100%)] text-slate-950"
  >
    <div class="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(15,23,42,0.12),_transparent_34%)]" />
    <div class="absolute -left-20 top-16 size-72 rounded-full bg-sky-200/50 blur-3xl" />
    <div class="absolute right-0 top-1/3 size-80 rounded-full bg-amber-200/45 blur-3xl" />

    <div class="relative mx-auto flex min-h-screen max-w-6xl items-center px-6 py-10">
      <div class="grid w-full gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <section class="rounded-[2rem] border border-slate-900/10 bg-white/85 p-8 shadow-[0_30px_100px_rgba(15,23,42,0.12)] backdrop-blur md:p-10">
          <div class="flex flex-wrap items-center gap-3">
            <span class="inline-flex items-center rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-white">
              {{ statusCode }}
            </span>
            <span class="text-sm font-medium text-slate-600">
              {{ statusMessage }}
            </span>
          </div>

          <h1 class="mt-6 max-w-2xl text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
            {{ title }}
          </h1>
          <p class="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
            {{ description }}
          </p>

          <div class="mt-8 flex flex-wrap gap-3">
            <Button size="lg" class="gap-2 cursor-pointer" @click="handleRetry">
              <RefreshCcwIcon class="size-4" />
              {{ retryLabel }}
            </Button>
            <Button size="lg" variant="outline" class="gap-2 cursor-pointer" @click="handleBack">
              <ArrowLeftIcon class="size-4" />
              {{ backLabel }}
            </Button>
          </div>

          <div class="mt-10 grid gap-4 md:grid-cols-3">
            <div class="rounded-2xl border border-slate-900/10 bg-slate-50/80 p-4">
              <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Status Code
              </p>
              <p class="mt-3 text-2xl font-semibold text-slate-900">
                {{ statusCode }}
              </p>
            </div>

            <div class="rounded-2xl border border-slate-900/10 bg-slate-50/80 p-4">
              <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Error Type
              </p>
              <p class="mt-3 text-lg font-semibold text-slate-900">
                {{ statusMessage }}
              </p>
            </div>

            <div class="rounded-2xl border border-slate-900/10 bg-slate-50/80 p-4">
              <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Request
              </p>
              <p class="mt-3 break-all font-mono text-sm text-slate-700">
                {{ request || 'Unavailable' }}
              </p>
            </div>
          </div>
        </section>

        <aside class="relative overflow-hidden rounded-[2rem] border border-slate-900/10 bg-slate-950 p-8 text-white shadow-[0_30px_100px_rgba(15,23,42,0.18)]">
          <div class="absolute inset-x-0 top-0 h-px bg-white/30" />
          <div class="absolute -right-14 top-12 size-40 rounded-full bg-sky-400/25 blur-3xl" />
          <div class="absolute -bottom-12 left-8 size-44 rounded-full bg-amber-300/20 blur-3xl" />

          <div class="relative flex h-full flex-col justify-between gap-10">
            <div>
              <div class="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2">
                <ShieldAlertIcon class="size-4" />
                <span class="text-sm font-medium text-white/90">Recovery Mode</span>
              </div>

              <p class="mt-8 text-sm font-semibold uppercase tracking-[0.38em] text-white/45">
                Calorie Tracker
              </p>
              <p class="mt-4 text-7xl font-semibold tracking-[-0.08em] text-white/95 sm:text-8xl">
                {{ statusCode }}
              </p>
            </div>

            <div class="space-y-4">
              <p class="text-xl font-semibold text-white/95">
                The current view was interrupted before it could finish rendering.
              </p>
              <p class="text-sm leading-7 text-white/70">
                Use retry to attempt the same navigation again, or return to a safe page and continue from there.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>
